import type { GameEvent, Phase } from '../core';
import { APPLAUSE_FILES, applauseForMove, pickApplause, type ApplausePool } from './applause';
import { chimeGain } from './bonusVoice';
import { CAST_FADE_SEC, accentFile } from './casts';
import {
  BOARD_SFX_GAIN,
  STUDIO_CLIPS,
  clipSources,
  matchPopFile,
  cascadeFile,
  recordedBoardFile,
  trickUiFile,
  trickUiGain,
  type TrickUi,
} from './cues';
import {
  CAST_GAIN,
  IDLE_AFTER_SEC,
  VOICE_DUCK_ATTACK_SEC,
  VOICE_DUCK_RELEASE_SEC,
  turnCallDelaySec,
} from './levels';
import type { VenueId } from '../core/venues';
import { venueSoundFile, venueSoundFiles } from '../fx/venueMatch';
import { CHEST_CLIPS, ChestCue, PRIZE_FANFARE_FILE } from './chest';
import { SpinCue } from './spinCue';
import { LobbyMusic } from './lobby';
import { Stingers, stingerForVoice, type StingerId } from './stingers';
import { TIMER_CUE_FILES, TIMER_ZERO_FILE, TimerCueGate, timerTickFile } from './timerCue';
import {
  TURN_CALL_TAKES,
  OPPONENT_TURN_FILE,
  noteTurnPhrase,
  pickRivalPhrase,
  pickTurnCall,
  pickTurnPhrase,
  turnCallGain,
  turnCallSources,
  turnCallTakes,
  type TurnCall,
  type TurnPhrase,
  type TurnPhraseState,
} from './turnCall';
import {
  voiceContest,
  voiceFile,
  voiceSources,
  VoiceDirector,
  VOICE_FILES,
  type VoiceId,
} from './voice';

export type SoundName = 'swap' | 'match' | 'special' | 'power' | 'win' | 'reject' | 'cascade' | 'ui' | 'tick';

/** Soft synthesized cues, plus the announcer lines. */
export class Sfx {
  muted = false;
  /** Separate from the master mute. The pause sheet writes these. */
  musicOn = true;
  effectsOn = true;
  voiceOn = true;
  /** Pause-sheet slider for chimes, stingers, and the announcer. */
  soundVolume = 1;
  /** Pause-sheet slider for the music bus. */
  musicVolume = 1;
  private sound: GainNode | null = null;
  private ctx: AudioContext | null = null;
  private noise: AudioBuffer | null = null;
  private master: GainNode | null = null;
  private chimes: GainNode | null = null;
  private readonly director = new VoiceDirector();
  private readonly clips = new Map<string, AudioBuffer>();
  private readonly clipLoads = new Map<string, Promise<AudioBuffer | null>>();
  private readonly trickClips = new Map<TrickUi, AudioBuffer>();
  private readonly trickLoads = new Map<TrickUi, Promise<AudioBuffer | null>>();
  private readonly named = new Map<string, AudioBuffer>();
  private readonly namedLoads = new Map<string, Promise<AudioBuffer | null>>();
  private readonly namedMiss = new Set<string>();
  private voiceSource: AudioBufferSourceNode | null = null;
  /** A turn-start callout. It occupies the speaker the same way a voiced line does. */
  private calloutSource: AudioBufferSourceNode | null = null;
  private active: { id: VoiceId; generation: number } | null = null;
  private generation = 0;
  private matchSpin = 0;
  private landAtMs = 0;
  private voicePending = false;
  private inDuel = false;
  private turnPlayer: 0 | 1 = 0;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private turnTimer: ReturnType<typeof setTimeout> | null = null;
  private turnToken = 0;
  private turnPending: { which: TurnCall; token: number; file?: string } | null = null;
  /** Last take each seat used, so the next callout does not repeat it. */
  private lastTurnCall: Partial<Record<TurnCall, string>> = {};
  /** Which of the two turn phrases each seat just heard, and how long that run is. */
  private turnPhrase: Record<TurnCall, TurnPhraseState> = {
    player: { phrase: null, run: 0 },
    rival: { phrase: null, run: 0 },
  };
  private castToken = 0;
  private castGain: GainNode | null = null;
  private castSources: AudioBufferSourceNode[] = [];
  private readonly castStop = new Set<ReturnType<typeof setTimeout>>();
  private readonly timerGate = new TimerCueGate();
  private timerSource: AudioBufferSourceNode | null = null;
  /** One cheer at a time. A request while this is set is dropped. */
  private applauseSource: AudioBufferSourceNode | null = null;
  /** A cheer has been chosen and is still loading. A louder one may replace it. */
  private applausePending: ApplausePool | null = null;
  private applauseToken = 0;
  /** Last file that started, so the next pick from that pool does not repeat it. */
  private lastApplause: string | null = null;
  /** Lobby and duel loops. Chimes and voice stay on the master, above the music bus. */
  private readonly lobby = new LobbyMusic(
    () => this.context(),
    () => this.masterBus(),
  );
  private readonly stingers = new Stingers(
    () => this.context(),
    () => this.soundBus(),
    () => this.muted || !this.effectsOn,
    (seconds) => this.lobby.duck(seconds),
    () => this.lobby.releaseDuck(),
  );
  private readonly chest = new ChestCue(
    () => this.context(),
    () => this.chimeBus(),
    () => this.muted || !this.effectsOn,
    (file) => this.loadNamed(file),
  );
  private readonly spin = new SpinCue(
    () => this.context(),
    () => this.chimeBus(),
    () => this.muted || !this.effectsOn,
    (file) => this.loadNamed(file),
  );

  unlock(): void {
    const ctx = this.context();
    if (ctx && ctx.state === 'suspended') void ctx.resume().then(() => this.lobby.kick());
    else this.lobby.kick();
  }

  /** Lobby loop only. The title scene starts it, so it stays on the first fetch. */
  preloadTitle(): void {
    this.lobby.preloadBed('lobby');
  }

  /**
   * Chest and prize cues. These are the sounds a player hears in the glen
   * before any duel, so the loader waits until they have been decoded.
   */
  preloadKey(): Promise<void> {
    return Promise.all([...CHEST_CLIPS, PRIZE_FANFARE_FILE].map((file) => this.loadNamed(file))).then(() => undefined);
  }

  /**
   * Voice lines, stingers, the duel loop, trick clicks, and board-cue probes.
   * Called once the title is on screen, not during boot.
   */
  preloadAfterTitle(): void {
    this.preloadVoices();
    this.stingers.preload();
    this.lobby.preloadBed('duel');
    (['select', 'deselect'] as const).forEach((which) => void this.loadTrick(which));
    for (const file of STUDIO_CLIPS) void this.loadNamed(file);
    for (const file of TIMER_CUE_FILES) void this.loadNamed(file);
    for (const file of APPLAUSE_FILES) void this.loadNamed(file);
    this.chest.preload();
    this.spin.preload();
    for (const which of ['player', 'rival'] as const) {
      for (const file of turnCallTakes(which)) void this.loadTurnFile(file);
    }
    void this.loadTurnFile(OPPONENT_TURN_FILE);
  }

  /** Decode both music loops. Kept for callers that want the whole bed at once. */
  preloadLobby(): void {
    this.lobby.preload();
  }

  /**
   * The three clips for this glen. Other venues stay unloaded so a phone
   * does not decode every match size up front.
   */
  preloadVenue(venueId: VenueId): void {
    for (const file of venueSoundFiles(venueId)) void this.loadNamed(file);
  }

  /**
   * Approved venue clip for this match size. A missing file falls back to the
   * studio pop so a glen is not silent. Called on the gather frame.
   */
  playVenueGather(venueId: VenueId, size: number): void {
    if (this.muted || !this.effectsOn) return;
    const file = venueSoundFile(venueId, size);
    const fallback = size >= 4 ? 'match4-pop' : 'match-pop-1';
    void this.playRecorded(file, BOARD_SFX_GAIN, () => {
      void this.playRecorded(fallback, BOARD_SFX_GAIN, () => this.playSynth('match'));
    });
  }

  /** Decode the win, loss, and power stingers. */
  preloadStingers(): void {
    this.stingers.preload();
  }

  /** A one-shot. Victory and defeat fade the music. The power stinger does not. */
  playStinger(id: StingerId): void {
    this.stingers.play(id);
  }

  /**
   * Menus share the lobby loop. A duel crossfades to the gameplay loop.
   * Results keep the duel loop. Coming back to a menu crossfades again.
   */
  enterScene(scene: string): void {
    const duel = scene === 'game';
    this.lobby.enter(scene, this.musicMuted());
    if (duel) {
      this.inDuel = true;
      this.lobby.holdDuelDuck(true);
      return;
    }
    this.inDuel = false;
    this.clearIdle();
    this.cancelTurnCall();
    this.lobby.holdDuelDuck(false);
    this.lobby.duckCast(false);
  }

  /** Duel music switch. Menus keep the lobby loop. */
  setDuelMusic(on: boolean): void {
    this.lobby.setDuelMusic(on);
  }

  /** Player turn start. One of "Your move!" or "Your turn!". */
  playerTurnStart(): void {
    this.beginSeat(0, 'player', 'your-turn');
  }

  /** Rival turn start. One of "Opponent's move!" or "Opponent's turn!". */
  rivalTurnStart(): void {
    this.beginSeat(1, 'rival');
  }

  /** A pointer in the duel counts as input, so a thinking pause can duck the bed. */
  noteActivity(): void {
    if (!this.inDuel || this.turnPlayer !== 0) return;
    this.lobby.setActivity('acting');
    this.armIdle();
  }

  /** Tile landing tick. A wave that lands together shares one play. */
  playTileLand(): void {
    const now = Date.now();
    if (now - this.landAtMs < 350) return;
    this.landAtMs = now;
    void this.playRecorded('tile-land', BOARD_SFX_GAIN, null);
  }

  /**
   * Start the shared cast bed and the class accent. endTrickCast trims them
   * if the animation stops first.
   */
  beginTrickCast(powerupId: string): void {
    if (this.muted || !this.effectsOn) return;
    const token = ++this.castToken;
    this.stopCastNow();
    this.lobby.duckCast(true);
    void this.startCast(powerupId, token);
  }

  /** Fade the cast if it is still playing. Match SFX are not touched. */
  endTrickCast(): void {
    const token = this.castToken;
    this.castToken += 1;
    this.fadeCast(CAST_FADE_SEC, token);
    this.lobby.duckCast(false);
  }

  /**
   * Board cues that have a studio file. Signature power stays synthesized.
   * `player` is who just moved. Only the human gets a cheer, and it is SFX
   * on the chime bus, so a voice line ducks it instead of blocking it.
   */
  playBoardEvent(event: GameEvent, player?: 0 | 1, options?: { quietMatch?: boolean }): void {
    if (event.type === 'swap') this.play('swap');
    else if (event.type === 'match') {
      if (!options?.quietMatch) {
        const file = matchPopFile(event.size, this.matchSpin);
        this.matchSpin += 1;
        void this.playRecorded(file, BOARD_SFX_GAIN, () => this.playSynth('match'));
      }
    } else if (event.type === 'cascade') {
      const steps = Math.min(12, Math.max(0, event.index));
      const rate = 2 ** (steps / 12);
      void this.playRecorded(cascadeFile(event.index), BOARD_SFX_GAIN, () => this.playSynth('cascade'), rate);
    } else if (event.type === 'special-created' || event.type === 'special-triggered' || event.type === 'hearth-refund') {
      this.play('special');
    } else if (event.type === 'power') this.play('power');
    if (player === undefined) return;
    const pool = applauseForMove(event, player);
    if (pool) this.cheer(pool);
  }

  /**
   * A cheer from the reward card. Same rules as a board cheer: sound effects
   * only, muted with the effects bus, and skipped when one is already playing.
   */
  playCheer(pool: ApplausePool): void {
    this.cheer(pool);
  }

  setMusic(on: boolean): void {
    this.musicOn = on;
    if (on) this.muted = false;
    this.lobby.setMuted(this.musicMuted());
  }

  setEffects(on: boolean): void {
    this.effectsOn = on;
    if (on) this.muted = false;
    if (this.muted || !this.effectsOn) {
      this.stingers.halt();
      this.stopTimerCue();
      this.stopApplause();
      this.chest.halt();
      this.spin.halt();
    }
  }

  setVoice(on: boolean): void {
    this.voiceOn = on;
    if (on) this.muted = false;
    if (this.muted || !this.voiceOn) this.haltVoice();
  }

  private musicMuted(): boolean {
    return this.muted || !this.musicOn;
  }

  /** Decode every wired line. The duel calls this again in case the title prefetch has not finished. */
  preloadBonusVoice(): void {
    this.preloadVoices();
  }

  preloadVoices(): void {
    for (const id of Object.keys(VOICE_FILES) as VoiceId[]) {
      for (let take = 0; take < VOICE_FILES[id].length; take++) void this.loadClip(id, take);
    }
  }

  /** A new duel forgets fill and last-move memory. Takes and the last result clip stay. */
  beginMatch(meterMax: number): void {
    this.director.beginMatch(meterMax);
  }

  /** The one result line for this screen. It is remembered only if the line actually starts. */
  nextResultId(scores: readonly [number, number], winner: 0 | 1): VoiceId {
    return this.director.pickResult(scores, winner);
  }

  setSoundVolume(amount: number): void {
    this.soundVolume = clamp01(amount);
    const bus = this.soundBus();
    if (bus) bus.gain.value = this.soundVolume;
  }

  setMusicVolume(amount: number): void {
    this.musicVolume = clamp01(amount);
    this.lobby.setLevel(this.musicVolume);
  }

  toggle(): boolean {
    this.muted = !this.muted;
    if (this.muted) {
      this.haltVoice();
      this.stingers.halt();
      this.stopTimerCue();
      this.stopApplause();
      this.chest.halt();
      this.spin.halt();
    } else this.unlock();
    this.lobby.setMuted(this.musicMuted());
    return this.muted;
  }

  /** Hold build. Stops on an early release or when the chest pops. */
  chestHold(): void {
    this.chest.hold();
  }

  /** Latch, lid, and the padlock, layered. The hold stops here. */
  chestOpen(): void {
    this.chest.open();
  }

  /** Prize Box reveal. Same hold cutoff, with the fanfare instead of the chest pop. */
  prizeOpen(): void {
    this.chest.prizeOpen();
  }

  /** Early release. Hold stops immediately and the cancel cue plays. */
  chestCancel(): void {
    this.chest.cancel();
  }

  /** Cancel stinger when no chest hold is running. The spin uses this. */
  chestCancelCue(): void {
    this.chest.playCancel();
  }

  /** Decode the wheel cues. Safe to call more than once. */
  preloadSpin(): void {
    this.spin.preload();
  }

  spinButton(): void {
    this.spin.button();
  }

  spinWindup(): void {
    this.spin.windup();
  }

  /** Daily Spin hold. The clip runs through the zing unless the player lets go. */
  spinWindback(): void {
    this.spin.windback();
  }

  /** Early release. Fades the wind-back out over about 30ms. */
  spinWindbackFade(): void {
    this.spin.fadeWind();
  }

  spinTick(random?: () => number): void {
    this.spin.tick(random);
  }

  spinSettle(): void {
    this.spin.settle();
  }

  /** `mid` is left to play out. The crown stops the bed before the fanfare. */
  spinWin(kind: 'common' | 'mid' | 'jackpot'): void {
    this.spin.win(kind);
  }

  spinBed(degPerSec: number): void {
    this.spin.bed(degPerSec);
  }

  spinBedStop(): void {
    this.spin.stopBed();
  }

  /** Drop a hold that has not popped, without the cancel cue and without cutting the pop. */
  chestDropHold(): void {
    this.chest.dropHold();
  }

  /** Trick card click. Select and deselect share TRICK_UI_GAIN. */
  playTrick(which: TrickUi): void {
    if (this.muted) return;
    const ctx = this.context();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
    const gain = trickUiGain(which);
    void this.loadTrick(which).then((buffer) => {
      if (!buffer || this.muted) return;
      this.playBuffer(buffer, gain);
    });
  }

  /** One file for the displayed second, from timer-tick-5 down to timer-tick-1. */
  playWarningTick(secondsLeft: number): void {
    const file = timerTickFile(secondsLeft);
    if (!file) return;
    this.playTimerCue(file);
  }

  /** The strip hit zero. Stops whatever tick is still playing, then plays timer-zero. */
  playTimeUp(): void {
    this.playTimerCue(TIMER_ZERO_FILE);
  }

  play(name: SoundName): void {
    if (this.muted || !this.effectsOn) return;
    const ctx = this.context();
    const bus = this.chimeBus();
    if (!ctx || !bus) return;
    if (ctx.state === 'suspended') void ctx.resume();
    const recorded = recordedBoardFile(name, this.matchSpin);
    if (recorded) {
      if (name === 'match') this.matchSpin += 1;
      void this.playRecorded(recorded, BOARD_SFX_GAIN, () => this.playSynth(name));
      return;
    }
    this.playSynth(name);
  }

  private playSynth(name: SoundName): void {
    if (this.muted || !this.effectsOn) return;
    const ctx = this.context();
    const bus = this.chimeBus();
    if (!ctx || !bus) return;
    const t = ctx.currentTime;
    if (name === 'ui') {
      this.blip(ctx, bus, 660, t, 0.05, 'sine', 0.03);
      this.blip(ctx, bus, 880, t + 0.045, 0.07, 'sine', 0.022);
    } else if (name === 'swap') {
      this.hiss(ctx, bus, t, 0.04, 0.025);
      this.blip(ctx, bus, 360, t, 0.07, 'triangle', 0.03, 210);
    } else if (name === 'match') {
      this.blip(ctx, bus, 523, t, 0.08, 'sine', 0.04);
      this.blip(ctx, bus, 659, t + 0.05, 0.1, 'sine', 0.032);
      this.blip(ctx, bus, 784, t + 0.1, 0.12, 'sine', 0.026);
    } else if (name === 'cascade') {
      this.blip(ctx, bus, 784, t, 0.08, 'sine', 0.036);
      this.blip(ctx, bus, 1046, t + 0.06, 0.11, 'triangle', 0.03);
      this.blip(ctx, bus, 1318, t + 0.12, 0.14, 'sine', 0.024);
    } else if (name === 'special') {
      this.hiss(ctx, bus, t, 0.08, 0.02);
      this.blip(ctx, bus, 392, t, 0.09, 'triangle', 0.028);
      this.blip(ctx, bus, 523, t + 0.06, 0.1, 'triangle', 0.028);
      this.blip(ctx, bus, 659, t + 0.12, 0.11, 'triangle', 0.026);
      this.blip(ctx, bus, 1046, t + 0.18, 0.16, 'sine', 0.024);
    } else if (name === 'power') {
      this.blip(ctx, bus, 262, t, 0.3, 'sine', 0.03, 523);
      this.blip(ctx, bus, 330, t + 0.02, 0.3, 'triangle', 0.018, 659);
      this.blip(ctx, bus, 392, t + 0.04, 0.34, 'sine', 0.018, 784);
    } else if (name === 'reject') {
      this.blip(ctx, bus, 196, t, 0.12, 'sine', 0.04, 130);
      this.hiss(ctx, bus, t, 0.05, 0.018);
    } else if (name === 'tick') {
      return;
    } else {
      [523, 659, 784, 1046].forEach((freq, index) => {
        this.blip(ctx, bus, freq, t + index * 0.11, 0.24, 'sine', 0.038);
      });
    }
  }

  /**
   * One announcer line, on the same context and mute as the chimes.
   * Chimes duck for the length of the clip. A line that is already playing,
   * including a turn-start callout, keeps the speaker. The new request is dropped.
   */
  playVoice(id: VoiceId): boolean {
    if (this.muted || !this.voiceOn) return false;
    const current = this.voiceOccupied() ? (this.active?.id ?? 'your-turn') : null;
    if (voiceContest(current, id) === 'skip') return false;
    const ctx = this.context();
    if (!ctx) return false;
    if (ctx.state === 'suspended') void ctx.resume();
    const take = this.director.peekTake(id);
    this.director.commit(id);
    const generation = ++this.generation;
    this.active = { id, generation };
    this.voicePending = true;
    void this.loadClip(id, take).then((buffer) => {
      if (this.generation !== generation || this.muted || !this.voiceOn || !buffer) {
        if (this.voicePending && this.generation === generation) this.voicePending = false;
        if (this.active?.generation === generation) {
          this.active = null;
          this.restoreChimes();
          this.lobby.releaseVoice();
        }
        this.pokeTurnCall();
        return;
      }
      this.startVoice(buffer, generation);
    });
    return true;
  }

  /** The "Bonus turn!" line. */
  playBonusVoice(): void {
    this.playVoice('bonus-turn');
  }

  /** Board event cues, including the bonus-turn banner and the next seat. */
  speakEvent(events: readonly GameEvent[], index: number): void {
    const event = events[index];
    const id = this.director.fromEvent(event, events, index);
    const stinger = stingerForVoice(id);
    if (stinger) this.playStinger(stinger);
    if (event?.type === 'turn-ended') {
      if (event.nextPlayer === 0) this.playerTurnStart();
      else this.rivalTurnStart();
      return;
    }
    if (id) this.playVoice(id);
  }

  speakLog(events: readonly GameEvent[]): void {
    events.forEach((_, index) => this.speakEvent(events, index));
  }

  /**
   * "Last move!" once, when the human has exactly one move they can still spend.
   */
  speakMoves(currentPlayer: 0 | 1, movesLeft: number, phase: Phase): void {
    const id = this.director.fromMoves(currentPlayer, movesLeft, phase);
    if (id) this.playVoice(id);
  }

  private startVoice(buffer: AudioBuffer, generation: number): void {
    const ctx = this.context();
    const master = this.soundBus();
    if (!ctx || !master || this.muted || !this.voiceOn || this.generation !== generation) return;
    if (this.voiceSource || this.calloutSource) {
      this.releaseVoiceSlot(generation);
      return;
    }
    this.voicePending = false;
    this.duckChimes(buffer.duration);
    this.lobby.duckVoice(buffer.duration);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(master);
    src.onended = () => {
      if (this.voiceSource === src) this.voiceSource = null;
      if (this.active?.generation === generation) this.active = null;
    };
    this.voiceSource = src;
    this.pokeTurnCall();
    src.start();
  }

  /** The clip never started. Free the slot without stopping a line that is speaking. */
  private releaseVoiceSlot(generation: number): void {
    if (this.voicePending && this.generation === generation) this.voicePending = false;
    if (this.active?.generation === generation) {
      this.active = null;
      this.restoreChimes();
      this.lobby.releaseVoice();
    }
    this.pokeTurnCall();
  }

  private haltVoice(): void {
    this.generation += 1;
    this.active = null;
    this.haltSource();
    this.haltCallout();
    this.voicePending = false;
    this.restoreChimes();
    this.lobby.releaseVoice();
    this.pokeTurnCall();
  }

  private haltSource(): void {
    const src = this.voiceSource;
    this.voiceSource = null;
    if (!src) return;
    try {
      src.stop();
    } catch {
      // Already finished.
    }
  }

  private haltCallout(): void {
    const src = this.calloutSource;
    this.calloutSource = null;
    if (!src) return;
    try {
      src.stop();
    } catch {
      // Already finished.
    }
  }

  /** A voiced line or a turn-start callout is in the speaker, or one is about to start. */
  private voiceOccupied(): boolean {
    return this.voicePending || this.active !== null || this.voiceSource !== null || this.calloutSource !== null;
  }

  private duckChimes(seconds: number): void {
    const bus = this.chimeBus();
    const ctx = this.ctx;
    if (!bus || !ctx) return;
    const now = ctx.currentTime;
    const ducked = chimeGain(true);
    const attack = VOICE_DUCK_ATTACK_SEC;
    const release = VOICE_DUCK_RELEASE_SEC;
    const down = now + Math.max(0.02, attack);
    const held = Math.max(down + 0.01, now + Math.max(0, seconds));
    bus.gain.cancelScheduledValues(now);
    bus.gain.setValueAtTime(bus.gain.value, now);
    bus.gain.linearRampToValueAtTime(ducked, down);
    bus.gain.setValueAtTime(ducked, held);
    bus.gain.linearRampToValueAtTime(chimeGain(false), held + release);
  }

  private restoreChimes(): void {
    const bus = this.chimes;
    const ctx = this.ctx;
    if (!bus || !ctx) return;
    const now = ctx.currentTime;
    bus.gain.cancelScheduledValues(now);
    bus.gain.setValueAtTime(chimeGain(false), now);
  }

  private loadClip(id: VoiceId, take: number): Promise<AudioBuffer | null> {
    const key = `${id}:${take}`;
    const cached = this.clips.get(key);
    if (cached) return Promise.resolve(cached);
    const pending = this.clipLoads.get(key);
    if (pending) return pending;
    const job = this.fetchClip(voiceFile(id, take)).then((buffer) => {
      if (buffer) this.clips.set(key, buffer);
      return buffer;
    });
    this.clipLoads.set(key, job);
    return job;
  }

  private loadTrick(which: TrickUi): Promise<AudioBuffer | null> {
    const cached = this.trickClips.get(which);
    if (cached) return Promise.resolve(cached);
    const pending = this.trickLoads.get(which);
    if (pending) return pending;
    const job = this.fetchFirst(clipSources(trickUiFile(which), this.oggM4aSupport())).then((buffer) => {
      if (buffer) this.trickClips.set(which, buffer);
      return buffer;
    });
    this.trickLoads.set(which, job);
    return job;
  }

  private beginSeat(player: 0 | 1, which: TurnCall, turnLine: 'your-turn' = 'your-turn'): void {
    this.inDuel = true;
    this.turnPlayer = player;
    if (player === 0) {
      this.lobby.setActivity('acting');
      this.armIdle();
    } else {
      this.clearIdle();
      this.lobby.setActivity('rival');
    }
    const prior = this.turnPhrase[which];
    const choice =
      which === 'rival'
        ? pickRivalPhrase(prior.phrase, prior.run, Math.random)
        : pickTurnPhrase(prior.phrase, prior.run, Math.random);
    if (which === 'rival') {
      const phrase: TurnPhrase = choice === 'opponents-turn' ? 'opponents-turn' : 'move';
      this.turnPhrase[which] = noteTurnPhrase(prior, phrase);
      this.scheduleTurnCall(which, phrase === 'opponents-turn' ? OPPONENT_TURN_FILE : undefined);
      return;
    }
    const heard: TurnPhrase = choice === 'turn' && this.playVoice(turnLine) ? 'turn' : 'move';
    this.turnPhrase[which] = noteTurnPhrase(prior, heard);
    if (heard === 'turn') {
      this.cancelTurnCall();
      return;
    }
    this.scheduleTurnCall(which);
  }

  private armIdle(): void {
    this.clearIdle();
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (!this.inDuel || this.turnPlayer !== 0) return;
      this.lobby.setActivity('idle');
    }, IDLE_AFTER_SEC * 1000);
  }

  private clearIdle(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  private scheduleTurnCall(which: TurnCall, file?: string): void {
    const token = ++this.turnToken;
    this.turnPending = file ? { which, token, file } : { which, token };
    this.armTurnCall(token);
  }

  private cancelTurnCall(): void {
    this.turnToken += 1;
    this.turnPending = null;
    if (this.turnTimer) clearTimeout(this.turnTimer);
    this.turnTimer = null;
  }

  private pokeTurnCall(): void {
    if (!this.turnPending) return;
    this.armTurnCall(this.turnPending.token);
  }

  private armTurnCall(token: number): void {
    if (this.turnTimer) clearTimeout(this.turnTimer);
    this.turnTimer = null;
    const pending = this.turnPending;
    if (!pending || pending.token !== token) return;
    if (this.voiceOccupied()) {
      this.cancelTurnCall();
      return;
    }
    const wait = turnCallDelaySec(0);
    this.turnTimer = setTimeout(() => {
      if (token !== this.turnToken || !this.turnPending) return;
      if (this.voiceOccupied()) {
        this.cancelTurnCall();
        return;
      }
      const which = this.turnPending.which;
      const file = this.turnPending.file;
      this.turnPending = null;
      this.turnTimer = null;
      void this.playTurnCall(which, file);
    }, wait * 1000);
  }

  private async playTurnCall(which: TurnCall, fixed?: string): Promise<void> {
    if (this.voiceOccupied()) return;
    if (fixed) {
      const buffer = await this.loadTurnFile(fixed);
      if (!buffer || this.muted || !this.voiceOn || this.voiceOccupied()) return;
      this.lastTurnCall[which] = fixed;
      this.playCallout(buffer, turnCallGain(fixed));
      return;
    }
    const last = this.lastTurnCall[which] ?? null;
    const first = pickTurnCall(which, last, Math.random);
    const order = [first, ...TURN_CALL_TAKES[which].filter((file) => file !== first)];
    for (const file of order) {
      const buffer = await this.loadTurnFile(file);
      if (!buffer) continue;
      if (this.muted || !this.voiceOn || this.voiceOccupied()) return;
      this.lastTurnCall[which] = file;
      this.playCallout(buffer, turnCallGain(file));
      return;
    }
  }

  /** Turn callouts are voice: they duck music and chimes, and they hold the speaker alone. */
  private playCallout(buffer: AudioBuffer, gain: number): void {
    const ctx = this.context();
    const master = this.soundBus();
    if (!ctx || !master || this.muted || !this.voiceOn || this.voiceOccupied()) return;
    if (ctx.state === 'suspended') void ctx.resume();
    this.duckChimes(buffer.duration);
    this.lobby.duckVoice(buffer.duration);
    this.playOnBus(buffer, gain, master, (src) => {
      this.calloutSource = src;
      const ended = src.onended;
      src.onended = (event) => {
        if (this.calloutSource === src) this.calloutSource = null;
        if (typeof ended === 'function') ended.call(src, event);
      };
    });
  }

  private async startCast(powerupId: string, token: number): Promise<void> {
    const [bed, accent] = await Promise.all([
      this.loadNamed('trick-cast-shared'),
      this.loadNamed(accentFile(powerupId)),
    ]);
    if (token !== this.castToken || this.muted || !this.effectsOn) return;
    const bus = this.castBus();
    if (!bus) return;
    const ctx = this.ctx;
    if (ctx) {
      bus.gain.cancelScheduledValues(ctx.currentTime);
      bus.gain.setValueAtTime(CAST_GAIN, ctx.currentTime);
    }
    if (bed) this.playOnBus(bed, 1, bus);
    if (accent) this.playOnBus(accent, 1, bus);
  }

  private castBus(): GainNode | null {
    const ctx = this.context();
    const master = this.soundBus();
    if (!ctx || !master) return null;
    if (!this.castGain) {
      this.castGain = ctx.createGain();
      this.castGain.gain.value = CAST_GAIN;
      this.castGain.connect(master);
    }
    return this.castGain;
  }

  private fadeCast(seconds: number, token: number): void {
    const ctx = this.ctx;
    const bus = this.castGain;
    if (!ctx || !bus) {
      this.stopCastNow();
      return;
    }
    const now = ctx.currentTime;
    bus.gain.cancelScheduledValues(now);
    bus.gain.setValueAtTime(bus.gain.value, now);
    bus.gain.linearRampToValueAtTime(0.0001, now + Math.max(0.02, seconds));
    const timer = setTimeout(() => {
      this.castStop.delete(timer);
      if (token + 1 !== this.castToken) return;
      this.stopCastNow();
      if (this.castGain && this.ctx) this.castGain.gain.setValueAtTime(CAST_GAIN, this.ctx.currentTime);
    }, Math.max(0.02, seconds) * 1000);
    this.castStop.add(timer);
  }

  private stopCastNow(): void {
    for (const timer of this.castStop) clearTimeout(timer);
    this.castStop.clear();
    for (const src of this.castSources) {
      try {
        src.stop();
      } catch {
        // Already finished.
      }
    }
    this.castSources = [];
  }

  /**
   * Stop the clock cue that is playing, then start this file.
   * A load that finishes after a newer cue has begun is dropped.
   */
  private playTimerCue(file: string): void {
    if (this.muted || !this.effectsOn) return;
    const ctx = this.context();
    const bus = this.chimeBus();
    if (!ctx || !bus) return;
    if (ctx.state === 'suspended') void ctx.resume();
    this.stopTimerCue();
    const token = this.timerGate.begin();
    void this.loadNamed(file).then((buffer) => {
      if (!this.timerGate.owns(token)) return;
      if (!buffer || this.muted || !this.effectsOn) return;
      this.stopTimerCue();
      const live = this.context();
      const dest = this.chimeBus();
      if (!live || !dest) return;
      const src = live.createBufferSource();
      const amp = live.createGain();
      amp.gain.setValueAtTime(1, live.currentTime);
      src.buffer = buffer;
      src.connect(amp);
      amp.connect(dest);
      src.onended = () => {
        if (this.timerSource === src) this.timerSource = null;
      };
      this.timerSource = src;
      try {
        src.start();
      } catch {
        if (this.timerSource === src) this.timerSource = null;
      }
    });
  }

  private stopTimerCue(): void {
    const src = this.timerSource;
    this.timerSource = null;
    if (!src) return;
    try {
      src.stop();
    } catch {
      // Already finished.
    }
  }

  /**
   * One cheer on the chime bus. A clip that is already audible blocks the
   * next one. A medium request can still replace a light request that has
   * not started, so a match-5 in the same wave is not stuck behind a short
   * cascade's quieter file.
   */
  private cheer(pool: ApplausePool): void {
    if (this.muted || !this.effectsOn || this.applauseSource) return;
    if (this.applausePending && (pool === 'light' || this.applausePending === 'medium')) return;
    const file = pickApplause(pool, this.lastApplause, Math.random);
    this.lastApplause = file;
    this.applausePending = pool;
    const token = ++this.applauseToken;
    void this.loadNamed(file).then((buffer) => {
      if (token !== this.applauseToken) return;
      this.applausePending = null;
      if (!buffer || this.muted || !this.effectsOn || this.applauseSource) return;
      this.startApplause(buffer);
    });
  }

  private startApplause(buffer: AudioBuffer): void {
    const ctx = this.context();
    const bus = this.chimeBus();
    if (!ctx || !bus || this.muted || !this.effectsOn) return;
    if (ctx.state === 'suspended') void ctx.resume();
    const src = ctx.createBufferSource();
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(1, ctx.currentTime);
    src.buffer = buffer;
    src.connect(amp);
    amp.connect(bus);
    src.onended = () => {
      if (this.applauseSource === src) this.applauseSource = null;
    };
    this.applauseSource = src;
    try {
      src.start();
    } catch {
      if (this.applauseSource === src) this.applauseSource = null;
    }
  }

  private stopApplause(): void {
    this.applauseToken += 1;
    this.applausePending = null;
    const src = this.applauseSource;
    this.applauseSource = null;
    if (!src) return;
    try {
      src.stop();
    } catch {
      // Already finished.
    }
  }

  private playRecorded(file: string, gain: number, fallback: (() => void) | null, rate = 1): Promise<void> {
    return this.loadNamed(file).then((buffer) => {
      if (!buffer) {
        fallback?.();
        return;
      }
      if (this.muted || !this.effectsOn) return;
      this.playBuffer(buffer, gain, rate);
    });
  }

  /**
   * Try the file once. A 404 is remembered for this session so a miss does not
   * refetch on every cue. A later drop-in is picked up on the next load.
   */
  private loadNamed(file: string): Promise<AudioBuffer | null> {
    const cached = this.named.get(file);
    if (cached) return Promise.resolve(cached);
    if (this.namedMiss.has(file)) return Promise.resolve(null);
    const pending = this.namedLoads.get(file);
    if (pending) return pending;
    const job = this.fetchFirst(clipSources(file, this.oggM4aSupport())).then((buffer) => {
      if (buffer) this.named.set(file, buffer);
      else this.namedMiss.add(file);
      this.namedLoads.delete(file);
      return buffer;
    });
    this.namedLoads.set(file, job);
    return job;
  }

  private loadTurnFile(file: string): Promise<AudioBuffer | null> {
    const cached = this.named.get(file);
    if (cached) return Promise.resolve(cached);
    if (this.namedMiss.has(file)) return Promise.resolve(null);
    const pending = this.namedLoads.get(file);
    if (pending) return pending;
    const job = this.fetchFirst(turnCallSources(file, this.oggM4aSupport())).then((buffer) => {
      if (buffer) this.named.set(file, buffer);
      else this.namedMiss.add(file);
      this.namedLoads.delete(file);
      return buffer;
    });
    this.namedLoads.set(file, job);
    return job;
  }

  private playOnBus(
    buffer: AudioBuffer,
    gain: number,
    bus: GainNode | null,
    claim?: (src: AudioBufferSourceNode) => void,
  ): AudioBufferSourceNode | null {
    const ctx = this.context();
    if (!ctx || !bus || this.muted) return null;
    const src = ctx.createBufferSource();
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(gain, ctx.currentTime);
    src.buffer = buffer;
    src.connect(amp);
    amp.connect(bus);
    if (bus === this.castGain) this.castSources.push(src);
    src.onended = () => {
      this.castSources = this.castSources.filter((item) => item !== src);
    };
    claim?.(src);
    try {
      src.start();
    } catch {
      // The context may have closed.
    }
    return src;
  }

  private playBuffer(buffer: AudioBuffer, gain: number, rate = 1): void {
    const ctx = this.context();
    const bus = this.chimeBus();
    if (!ctx || !bus || this.muted) return;
    const src = ctx.createBufferSource();
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(gain, ctx.currentTime);
    src.buffer = buffer;
    src.playbackRate.setValueAtTime(rate > 0 ? rate : 1, ctx.currentTime);
    src.connect(amp);
    amp.connect(bus);
    try {
      src.start();
    } catch {
      // The context may have closed.
    }
  }

  private async fetchFirst(urls: string[]): Promise<AudioBuffer | null> {
    const ctx = this.context();
    if (!ctx || typeof fetch !== 'function') return null;
    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const data = await response.arrayBuffer();
        return await ctx.decodeAudioData(data.slice(0));
      } catch {
        // Try the other file.
      }
    }
    return null;
  }

  private async fetchClip(file: string): Promise<AudioBuffer | null> {
    return this.fetchFirst(this.sourceOrder(file));
  }

  private oggM4aSupport(): { ogg: boolean; m4a: boolean } {
    if (typeof document === 'undefined') return { ogg: true, m4a: true };
    const audio = document.createElement('audio');
    const yes = (mime: string) => {
      const value = audio.canPlayType(mime);
      return value === 'probably' || value === 'maybe';
    };
    return {
      ogg: yes('audio/ogg; codecs="vorbis"'),
      m4a: yes('audio/mp4; codecs="mp4a.40.2"'),
    };
  }

  private sourceOrder(file: string): string[] {
    if (typeof document === 'undefined') return voiceSources(file, { mp3: true, ogg: false });
    const audio = document.createElement('audio');
    const yes = (mime: string) => {
      const value = audio.canPlayType(mime);
      return value === 'probably' || value === 'maybe';
    };
    return voiceSources(file, {
      mp3: yes('audio/mpeg'),
      ogg: yes('audio/ogg; codecs="vorbis"'),
      m4a: yes('audio/mp4; codecs="mp4a.40.2"'),
    });
  }

  private context(): AudioContext | null {
    if (typeof AudioContext === 'undefined') return null;
    try {
      if (!this.ctx) this.ctx = new AudioContext();
    } catch {
      return null;
    }
    return this.ctx;
  }

  private masterBus(): GainNode | null {
    const ctx = this.context();
    if (!ctx) return null;
    if (!this.master) {
      this.master = ctx.createGain();
      this.master.gain.value = 1;
      this.master.connect(ctx.destination);
    }
    return this.master;
  }

  private soundBus(): GainNode | null {
    const ctx = this.context();
    const master = this.masterBus();
    if (!ctx || !master) return null;
    if (!this.sound) {
      this.sound = ctx.createGain();
      this.sound.gain.value = this.soundVolume;
      this.sound.connect(master);
    }
    return this.sound;
  }

  private chimeBus(): GainNode | null {
    const ctx = this.context();
    const master = this.soundBus();
    if (!ctx || !master) return null;
    if (!this.chimes) {
      this.chimes = ctx.createGain();
      this.chimes.gain.value = 1;
      this.chimes.connect(master);
    }
    return this.chimes;
  }

  private blip(
    ctx: AudioContext,
    bus: GainNode,
    freq: number,
    when: number,
    duration: number,
    type: OscillatorType,
    gain: number,
    slideTo?: number,
  ): void {
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2800, when);
    osc.type = type;
    osc.frequency.setValueAtTime(freq, when);
    if (slideTo && slideTo > 0) osc.frequency.exponentialRampToValueAtTime(slideTo, when + duration);
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.exponentialRampToValueAtTime(gain, when + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    osc.connect(filter);
    filter.connect(amp);
    amp.connect(bus);
    osc.start(when);
    osc.stop(when + duration + 0.02);
  }

  private hiss(ctx: AudioContext, bus: GainNode, when: number, duration: number, gain: number): void {
    const buffer = this.noiseBuffer(ctx, duration);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(900, when);
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(gain, when);
    amp.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    src.connect(filter);
    filter.connect(amp);
    amp.connect(bus);
    src.start(when);
    src.stop(when + duration);
  }

  private noiseBuffer(ctx: AudioContext, duration: number): AudioBuffer {
    if (this.noise && this.noise.duration >= duration) return this.noise;
    const length = Math.max(1, Math.floor(ctx.sampleRate * 0.2));
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.noise = buffer;
    return buffer;
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}

export const sfx = new Sfx();
